/**
 * ThesisDraftBar — docked row for the live thesis card (spec §4.2): symbol +
 * fill progress, tap to open ThesisDraftSheet. The saved state flips the row
 * to a check label; the ✕ dismisses the bar (the card stays in the transcript
 * and can be re-opened from there — the bar is a surface, not the store).
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { cardProgress } from '../../services/thesisCard';
import type { ThesisCard } from '../../services/chatStream';

export function ThesisDraftBar({
  card,
  saved,
  onOpen,
  onDismiss,
  onBuildTargetPrice,
}: {
  card: ThesisCard;
  saved: boolean;
  onOpen: () => void;
  onDismiss?: () => void;
  /** T15 entry point 2: offered on the rendered thesis card whenever the
   * owning screen can resolve the card's concrete symbol and no target-price
   * draft is already active. Absent → the bar renders exactly as before. */
  onBuildTargetPrice?: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { filled, total } = cardProgress(card);

  return (
    <View
      testID="thesis-draft-bar"
      style={[
        styles.row,
        { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
      ]}
    >
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`${card.symbol} ${t('chat.thesisDraft', { defaultValue: 'Thesis draft' })}`}
        style={styles.main}
      >
        <Text style={[styles.symbol, { color: theme.colors.primary }]}>▣ {card.symbol}</Text>
        {saved ? (
          <Text style={[styles.meta, { color: theme.colors.success }]}>
            ✓ {t('chat.thesisSaved', { defaultValue: 'Thesis saved' })}
          </Text>
        ) : (
          <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
            {t('chat.thesisDraft', { defaultValue: 'Thesis draft' })} ◔ {filled}/{total} ·{' '}
            {t('chat.tapToView', { defaultValue: 'Tap to view' })}
          </Text>
        )}
      </Pressable>
      {/* T15 entry point 2: the thesis card's own target-price opener. The
          callback is passed only when the card carries a concrete symbol and
          no target-price draft owns the slot (ChatConversation gates both),
          so the affordance never sends the null-symbol command the backend
          thesis hard-gate would refuse. */}
      {onBuildTargetPrice && (
        <Pressable
          testID="thesis-build-target-price"
          onPress={onBuildTargetPrice}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={`${t('chat.targetPrice.build', {
            defaultValue: 'Build target price',
          })} — ${card.symbol}`}
          style={[styles.buildTp, { borderColor: theme.colors.border }]}
        >
          <Text style={[styles.buildTpText, { color: theme.colors.primary }]}>
            🎯 {t('chat.targetPrice.build', { defaultValue: 'Build target price' })}
          </Text>
        </Pressable>
      )}
      <Pressable
        testID="thesis-draft-dismiss"
        onPress={onDismiss}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
        style={styles.dismiss}
      >
        <Text style={[styles.dismissText, { color: theme.colors.textTertiary }]}>✕</Text>
      </Pressable>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      borderTopWidth: 1,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    main: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
    },
    symbol: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    meta: {
      fontSize: theme.typography.fontSizeSm,
      flexShrink: 1,
    },
    buildTp: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
      borderWidth: 1,
      borderRadius: theme.radii.sm,
      marginHorizontal: theme.spacing.xs,
    },
    buildTpText: {
      fontSize: theme.typography.fontSizeSm,
    },
    dismiss: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
